import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { AlertCircle, CheckCircle2, Library, Loader2 } from 'lucide-react';
import Modal from 'components/ui/Modal';
import { Button } from 'components/ui/button';
import { useToast } from 'components/ui/use-toast';
import { cn } from 'lib/utils';
import { SubjectSelect } from './SubjectSelect';
import { useSubjectList } from 'modules/subject';
import type { Subject } from 'modules/subject/types';
import * as LearnArtifacts from 'modules/learnArtifacts';
import type { BankDraftQuestion, LearnArtifact } from 'modules/learnArtifacts';
import { GeneratedQuestionCard } from './GeneratedQuestionCard';
import { QuestionCardBoundary } from './QuestionCardBoundary';

/** How long editing stays quiet before the draft is persisted. */
const AUTOSAVE_DEBOUNCE_MS = 900;

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export interface GeneratedQuestionsDialogProps {
  open: boolean;
  onClose: () => void;
  artifact: LearnArtifact | null;
  /** True once these questions are already in the bank — the draft turns read-only. */
  banked: boolean;
}

/**
 * Full-bleed review of an AI-generated question batch.
 *
 * Every question renders the way a candidate will see it (QuestionPreview), can
 * be edited in place through the same inputs as single-question authoring, and
 * is included or excluded before a single commit.
 *
 * Committing is ONE-SHOT: the bank deduplicates on `sourceArtifactId`, so a
 * second commit of the questions left out here would be skipped entirely. The
 * header says so before the fact and the draft goes read-only after.
 */
export const GeneratedQuestionsDialog: React.FC<GeneratedQuestionsDialogProps> = ({
  open,
  onClose,
  artifact,
  banked,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [questions, setQuestions] = useState<BankDraftQuestion[]>([]);
  // Selection is a commit argument, not a property of the question, so it lives
  // in session state and resets to "everything" each time the dialog opens.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  // Stored questions the client schema couldn't read. Shown inline so they are
  // never silently missing from the count the author is committing.
  const [rejected, setRejected] = useState<LearnArtifacts.RejectedDraftQuestion[]>([]);
  // Destination is chosen HERE, not at generation — you can only judge where
  // questions belong once you can see them.
  const [subjectId, setSubjectId] = useState('');

  const { data: subjects = [] } = useSubjectList();

  const generatedForSubjectId = useMemo(() => {
    const bank = artifact?.metadata?.bank as { subjectId?: string } | undefined;
    return bank?.subjectId ?? '';
  }, [artifact]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Quiz drafts carry a narrower payload (2 types, plain text, no points or
  // difficulty). They review through this same dialog so "Review" behaves
  // identically everywhere; the controls quizSpec can't store are disabled
  // rather than silently dropped on save.
  const isQuizDraft = artifact?.type === 'quiz';

  // Seed from the artifact whenever a different draft is opened.
  useEffect(() => {
    if (!open || !artifact) return;
    const quiz = LearnArtifacts.parseQuizPayload(artifact.payload);
    // Bank drafts parse per question: one unreadable question must not cost the
    // other nine. `rejected` carries the exact reason for each that failed.
    const bank = quiz
      ? { questions: LearnArtifacts.quizSpecToDraft(quiz), rejected: [] }
      : LearnArtifacts.parseBankDraftQuestions(artifact.payload);
    const items = bank.questions;

    setRejected(bank.rejected);
    if (bank.rejected.length) {
      // eslint-disable-next-line no-console
      console.error(
        `[GeneratedQuestionsDialog] artifact ${artifact.id}: ` +
          `${bank.rejected.length} of ${items.length + bank.rejected.length} question(s) ` +
          `failed the client draft schema.\n` +
          bank.rejected
            .map((r) => `  #${r.index + 1} → ${r.issues.join('; ')}\n     ${JSON.stringify(r.raw)}`)
            .join('\n'),
      );
    }

    setQuestions(items);
    setSelectedIds(new Set(items.map((q) => q.id)));
    setEditingId(null);
    setSaveState('idle');
    // Pre-fill only if generation was pointed at a subject; otherwise the author
    // picks one before committing.
    setSubjectId(generatedForSubjectId);
  }, [open, artifact, generatedForSubjectId]);

  // ---- Autosave --------------------------------------------------------
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Skips the save that would otherwise fire from merely seeding the state.
  const dirty = useRef(false);

  const persist = useCallback(
    async (next: BankDraftQuestion[]) => {
      if (!artifact) return;
      setSaveState('saving');
      try {
        // Each draft kind round-trips into its own stored shape; the API
        // validates against that shape on every write.
        const payload = isQuizDraft
          ? (LearnArtifacts.draftToQuizSpec(artifact.title, next) as unknown as Record<string, unknown>)
          : { questions: next };
        await LearnArtifacts.Api.updateArtifactPayload(artifact.id, payload);
        setSaveState('saved');
      } catch {
        // Keep what's on screen — reverting would silently discard the author's
        // work, which is worse than an unsaved warning.
        setSaveState('error');
      }
    },
    [artifact, isQuizDraft],
  );

  useEffect(() => {
    if (!dirty.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(questions), AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [questions, persist]);

  // Reset the dirty latch when the dialog closes so reopening doesn't resave.
  useEffect(() => {
    if (!open) dirty.current = false;
  }, [open]);

  const updateQuestion = (id: string, patch: Partial<BankDraftQuestion>) => {
    dirty.current = true;
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== id) return q;
        const next = { ...q, ...patch };
        // `undefined` in a patch means "clear this field" (a type switch does
        // exactly that); drop those keys rather than persisting nulls.
        for (const [key, value] of Object.entries(patch)) {
          if (value === undefined) delete (next as Record<string, unknown>)[key];
        }
        return next;
      }),
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    dirty.current = true;
    setQuestions((prev) => {
      const from = prev.findIndex((q) => q.id === active.id);
      const to = prev.findIndex((q) => q.id === over.id);
      if (from === -1 || to === -1) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const toggleSelected = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // ---- Commit ----------------------------------------------------------
  const commit = useMutation({
    mutationFn: async () => {
      if (!artifact) throw new Error('No draft selected');
      if (!subjectId) throw new Error('No subject selected');
      // Flush pending edits first so the bank copy matches what's on screen.
      if (saveTimer.current) clearTimeout(saveTimer.current);
      if (dirty.current) {
        await persist(questions);
        dirty.current = false;
      }
      return LearnArtifacts.Api.saveArtifactToBank(artifact.id, {
        subjectId,
        questionIds: questions.filter((q) => selectedIds.has(q.id)).map((q) => q.id),
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['question-bank'] });
      queryClient.invalidateQueries({ queryKey: ['subjects'] });
      queryClient.invalidateQueries({ queryKey: ['learn-artifacts'] });
      if (result.questionsAdded > 0) {
        toast.success(t('saved_to_bank'), {
          description: t('created_questions_count', { count: result.questionsAdded }),
        });
      } else {
        toast.info(t('already_in_bank'));
      }
      onClose();
    },
    onError: (error: unknown) =>
      toast.error(t('save_failed'), {
        description: (error as Error)?.message || t('please_try_again'),
      }),
  });

  const selectedCount = questions.filter((q) => selectedIds.has(q.id)).length;
  const canCommit = !banked && selectedCount > 0 && Boolean(subjectId) && !commit.isPending;

  /** Why the commit button is disabled, stated rather than left to guesswork. */
  const blockedReason = banked
    ? null
    : selectedCount === 0 && questions.length > 0
      ? t('select_at_least_one_question')
      : !subjectId && questions.length > 0
        ? t('choose_a_subject_to_save_into')
        : null;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={artifact?.title || t('review_generated_questions')}
      size="full"
    >
      {/* Toolbar — sticks to the top of the scrolling body. Negative margins
          cancel the Modal's content padding so it spans the full width. */}
      <div className="sticky top-0 z-10 -mx-4 -mt-4 mb-2 flex flex-wrap items-center gap-3 border-b border-border bg-background px-4 py-2.5 sm:-mx-6 sm:-mt-6 sm:px-6">
        <span className="text-sm font-medium text-foreground">
          {banked
            ? t('n_questions_in_bank', { count: questions.length })
            : t('n_of_m_selected', { selected: selectedCount, total: questions.length })}
        </span>

        {!banked && (
          <span
            className={cn(
              'inline-flex items-center gap-1.5 text-xs',
              saveState === 'error' ? 'text-error-600' : 'text-muted-foreground',
            )}
          >
            {saveState === 'saving' && (
              <>
                <Loader2 className="h-3 w-3 animate-spin" /> {t('editor_saving')}
              </>
            )}
            {saveState === 'saved' && (
              <>
                <CheckCircle2 className="h-3 w-3" /> {t('saved')}
              </>
            )}
            {saveState === 'error' && (
              <>
                <AlertCircle className="h-3 w-3" /> {t('draft_not_saved')}
              </>
            )}
          </span>
        )}

        <span className="flex-1" />

        {/* Destination — the "where do these go" decision, made with the
            questions in front of you rather than before they exist. */}
        {!banked && (
          <div className="flex items-center gap-1.5 max-w-full">
            <label htmlFor="draft-subject" className="text-xs text-muted-foreground shrink-0">
              {t('save_to')}
            </label>
            <SubjectSelect
              id="draft-subject"
              value={subjectId}
              onChange={setSubjectId}
              subjects={subjects as Subject[]}
              showCount
              triggerClassName="h-auto min-h-8 min-w-[200px] max-w-[320px] text-xs"
            />
          </div>
        )}

        {banked ? (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-success-200 bg-success-50 px-2.5 py-1 text-xs font-medium text-success-700 dark:border-success-900 dark:bg-success-900/30 dark:text-success-300">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {t('saved_to_bank')}
          </span>
        ) : (
          <Button size="sm" onClick={() => commit.mutate()} disabled={!canCommit}>
            {commit.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Library className="mr-2 h-4 w-4" />
            )}
            {t('add_n_to_bank', { count: selectedCount })}
          </Button>
        )}
      </div>

      {blockedReason && (
        <p className="mb-2 text-xs text-amber-600 dark:text-amber-500">{blockedReason}</p>
      )}

      {/* Commit is one-shot — say so before it happens, not after. */}
      {!banked && questions.length > 0 && (
        <p className="mb-3 text-xs text-muted-foreground">{t('add_to_bank_is_final')}</p>
      )}

      {/* Never let unreadable questions go unmentioned — the author is about to
          commit a count, and a silently smaller one is worse than an error. */}
      {rejected.length > 0 && questions.length > 0 && (
        <p className="mb-3 rounded-md border border-warning-300 bg-warning-50 px-3 py-2 text-xs text-warning-800 dark:border-warning-900 dark:bg-warning-900/20 dark:text-warning-200">
          {t('n_questions_could_not_be_displayed', { count: rejected.length })}
        </p>
      )}

      <div className="mx-auto w-full max-w-3xl">
        {questions.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            {rejected.length ? t('draft_could_not_be_read') : t('no_questions_in_draft')}
          </p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={questions.map((q) => q.id)}
              strategy={verticalListSortingStrategy}
            >
              {questions.map((question, index) => (
                <QuestionCardBoundary
                  key={question.id}
                  index={index + 1}
                  type={question.type}
                >
                  <GeneratedQuestionCard
                    question={question}
                    index={index}
                    selected={selectedIds.has(question.id)}
                    editing={editingId === question.id}
                    locked={banked}
                    limitedPayload={isQuizDraft}
                    subjectId={subjectId}
                    onToggleSelected={() => toggleSelected(question.id)}
                    onToggleEditing={() =>
                      setEditingId((prev) => (prev === question.id ? null : question.id))
                    }
                    onChange={(patch) => updateQuestion(question.id, patch)}
                  />
                </QuestionCardBoundary>
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>
    </Modal>
  );
};

export default GeneratedQuestionsDialog;
